import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import type { DriverRow } from '../lib/types';
import { TYPE_LABEL } from '../components/DriverPolicies';
import { X } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

/** 只取这一屏要用的两个字段 —— 判断排班到底起不起作用 */
interface PolicyFlag {
  driverType: 'EMPLOYEE' | 'CONTRACTOR' | 'CROWD';
  requireShift: boolean;
  driverCount: number;
}

interface Shift {
  id: string;
  driver: { id: string; name: string; driverNo: string };
  startsAt: string;
  endsAt: string;
  status: string;
  statusLabel: string;
  lateMinutes: number;
  note: string | null;
  /**
   * CONCRETE = 库里的一条真班次;RULE = 固定排班算出来的。
   *
   * ★ 老版本服务端没有这个字段,所以可选 —— 缺了当 CONCRETE。
   */
  source?: 'CONCRETE' | 'RULE';
  ruleId?: string;
}

/** 固定排班规则 —— 「每周六」这种,长期有效 */
interface ShiftRule {
  id: string;
  driver: { id: string; name: string; driverNo: string };
  weekdays: number[];
  startTime: string;
  endTime: string;
  effectiveFrom: string;
  /** null = 无限期 */
  effectiveTo: string | null;
  overnight: boolean;
  note: string | null;
}

const STATUS_STYLE: Record<string, string> = {
  PLANNED: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
  CHECKED_IN: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400',
  CHECKED_OUT: 'bg-slate-100 dark:bg-slate-800 text-slate-400',
  ABSENT: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400',
};

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

/** 本周一(按本地时区看就够了 —— 这只是界面上的分栏) */
function mondayOf(offsetWeeks: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // 周一 = 0
  d.setDate(d.getDate() - dow + offsetWeeks * 7);
  return d;
}

const iso = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const hhmm = (s: string) =>
  new Date(s).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });

/**
 * 排班。
 *
 * ★ 做成**周视图的表格**,而不是一张班次列表。
 *
 *   排班这件事人是按「这一周谁哪天上班」来想的 ——
 *   列表按时间排的话,「周三谁没人」这个最常见的问题要自己在脑子里拼。
 *   一眼能看出哪个格子是空的,才叫排班表。
 */
export function Shifts({ drivers, permissions }: { drivers: DriverRow[]; permissions: string[] }) {
  const [week, setWeek] = useState(0);
  const [rows, setRows] = useState<Shift[]>([]);
  const [rules, setRules] = useState<ShiftRule[]>([]);
  /**
   * 「必须排班」开着的是哪几类。
   *
   * ★ 加这个是因为踩过:店里配了 6 条固定排班,而三种类型的「必须排班」
   *   全是关的 —— 排班一点作用都没有,司机照常看得到可领取。
   *   用户报的原话:「没有排班,或者是当日没有排班的情况下,司机还是可以
   *   看到订单,不知道是不是因为缓存还是」。
   *
   *   问题不在缓存,在于**这一屏从来没说过它到底生效没有**:
   *   人配完一堆排班,看不出它是不是在管事。
   */
  const [policies, setPolicies] = useState<PolicyFlag[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);

  const canManage = permissions.includes('shift:manage');
  const monday = mondayOf(week);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = mondayOf(week);
      const to = new Date(from);
      to.setDate(to.getDate() + 7);
      const [shifts, ruleRows, policyRows] = await Promise.all([
        api<Shift[]>(`/shifts?from=${iso(from)}&to=${iso(to)}&limit=500`),
        api<ShiftRule[]>('/shifts/rules'),
        /*
         * ★ 读不到策略就当「不知道」(null),而不是当「都关着」——
         *   当成关着会给没权限看策略的人挂一条错的警告。
         */
        api<PolicyFlag[]>('/driver-policies').catch(() => null),
      ]);
      setRows(shifts);
      setRules(ruleRows);
      setPolicies(policyRows);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到排班表');
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(s: Shift) {
    if (!confirm(`删掉 ${s.driver.name} ${hhmm(s.startsAt)} 的班次?`)) return;
    try {
      await api(`/shifts/${s.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '删不掉');
    }
  }

  /**
   * 停掉一条固定排班。
   *
   * ★ 说清楚**影响的是以后所有这个周几**,不是某一天。
   *   一条规则顶一辈子的班,而按钮长得和删一个班次一模一样 ——
   *   不问清楚的话,想请一天假的人会把整条长期排班删掉。
   */
  async function removeRule(r: ShiftRule) {
    const when = r.weekdays.map((w) => `周${WEEKDAY[w]}`).join('、');
    if (
      !confirm(
        `停掉 ${r.driver.name} 的固定排班(${when} ${r.startTime}–${r.endTime})?\n\n` +
          '以后每一个这些星期几都不再算他排了班。只是想空出某一天的话,现在还没有这个功能 —— ' +
          '可以给这条规则填一个截止日期,再用「指定日期」补上后面要上的那几天。',
      )
    ) {
      return;
    }
    try {
      await api(`/shifts/rules/${r.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '停不掉');
    }
  }

  /*
   * ★ 只算**真的有司机**的那几类。
   *   一类司机都没有的类型开没开「必须排班」都无所谓,
   *   把它算进去会让警告在一个配置正确的店里也亮着 —— 那种警告很快就被无视了。
   */
  const offTypes =
    policies === null ? null : policies.filter((p) => p.driverCount > 0 && !p.requireShift);

  const active = drivers.filter((d) => d.status === 'ACTIVE');
  const byDriver = new Map<string, Shift[]>();
  for (const s of rows) {
    const list = byDriver.get(s.driver.id) ?? [];
    list.push(s);
    byDriver.set(s.driver.id, list);
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-1 flex items-center justify-between">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">
          排班
        </h1>
        {canManage && (
          <button
            onClick={() => setPlanning(true)}
            className="h-10 px-4 rounded-lg bg-brand-600 text-white text-sm font-medium"
          >
            + 排班
          </button>
        )}
      </div>
      <p className="mb-4 text-sm text-slate-500">
        司机策略里开了「必须排班」时,当天没排班的司机看不到「可领取」,也上不了线 ——
        <span className="text-slate-400">派给他的单不受影响,照常能接。</span>
        {/*
          ★ 「时段不起作用」必须写出来。
 
            现在不打卡,可领取和上线判的都是「这一天有没有排班」——
            时段(09:00–19:00)**对行为没有任何影响**。
            不说的话,填了 19:00 的人会以为系统到点会管,
            而它不管;等他发现的时候已经按这个假设安排了一天的活。
        */}
        <span className="mt-0.5 block text-slate-400">
          按「这一天有没有排班」算,<b>不看时段</b> —— 下面填的上下班时间只是给你自己的记录,
          现在司机不打卡,到点系统不会做任何事。
        </span>
      </p>

      {/*
        ★ 「这些排班到底起不起作用」必须写在这一屏上。

          真事:店里配了 6 条固定排班,而三种类型的「必须排班」全是关的 ——
          排班一点作用都没有,司机照常看得到可领取。而这一屏从来没说过
          它生效没有,于是人只能怀疑是缓存。用户的原话:
          「没有排班,或者是当日没有排班的情况下,司机还是可以看到订单,
          不知道是不是因为缓存还是」。

          排完班却什么都没发生,是这个功能最容易出现的失败 —— 而它**完全**
          可以在这里说出来。
      */}
      {offTypes !== null &&
        offTypes.length === (policies ?? []).filter((p) => p.driverCount > 0).length && (
          <div
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm
                     dark:border-amber-900 dark:bg-amber-950/30"
          >
            <div className="font-medium text-amber-900 dark:text-amber-200">
              这些排班现在不起作用
            </div>
            <div className="mt-1 text-amber-800/90 dark:text-amber-200/80">
              所有司机类型的「必须排班」都是关的,所以谁都能照常上线、照常看到「可领取」——
              不管有没有排班。要让排班管事,去
              <span className="font-medium">设置 → 司机策略</span>,把「必须排班」打开。
            </div>
          </div>
        )}
      {offTypes !== null &&
        offTypes.length > 0 &&
        offTypes.length < (policies ?? []).filter((p) => p.driverCount > 0).length && (
          <div className="mb-4 text-sm text-amber-700 dark:text-amber-400">
            {offTypes.map((t) => TYPE_LABEL[t.driverType] ?? t.driverType).join('、')}
            这几类司机不看排班(他们的「必须排班」是关的)—— 给他们排的班不影响任何事。
          </div>
        )}

      <div className="mb-4 flex items-center gap-2">
        <button
          onClick={() => setWeek(week - 1)}
          className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-600 text-sm"
        >
          ‹ 上一周
        </button>
        <button
          onClick={() => setWeek(0)}
          className={`h-9 px-3 rounded-lg border text-sm ${
            week === 0
              ? 'border-brand-600 text-brand-600 dark:text-brand-500'
              : 'border-slate-300 dark:border-slate-600'
          }`}
        >
          本周
        </button>
        <button
          onClick={() => setWeek(week + 1)}
          className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-600 text-sm"
        >
          下一周 ›
        </button>
        <span className="text-sm text-slate-500 ml-2">
          {iso(days[0]!)} ~ {iso(days[6]!)}
        </span>
        {loading && <span className="text-sm text-slate-400">加载中…</span>}
      </div>

      {error && (
        <div
          className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-2 text-red-700 dark:text-red-300 text-sm"
        >
          {error}
        </div>
      )}

      <div
        className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                      bg-white dark:bg-slate-800"
      >
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
            <tr>
              <th className="p-3 text-left font-medium w-40">司机</th>
              {days.map((d) => {
                const today = iso(d) === iso(new Date());
                return (
                  <th
                    key={iso(d)}
                    className={`p-2 text-center font-medium ${
                      today ? 'text-brand-600 dark:text-brand-500' : ''
                    }`}
                  >
                    周{WEEKDAY[d.getDay()]}
                    <div className="text-xs font-normal text-slate-400">
                      {d.getMonth() + 1}/{d.getDate()}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {active.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 dark:border-slate-700/50">
                <td className="p-3">
                  <div className="text-slate-900 dark:text-slate-100">{d.name}</div>
                  <div className="text-xs text-slate-400 font-mono">{d.driverNo}</div>
                </td>
                {days.map((day) => {
                  const dayIso = iso(day);
                  const cell = (byDriver.get(d.id) ?? []).filter((s) => {
                    if (s.source === 'RULE') {
                      return s.id.endsWith(`:${dayIso}`);
                    }
                    return (
                      new Date(s.startsAt).toDateString() === day.toDateString() ||
                      iso(new Date(s.startsAt)) === dayIso
                    );
                  });
                  return (
                    <td key={dayIso} className="p-1.5 align-top text-center">
                      {cell.length === 0 ? (
                        // ★ 空格子就留空,不写「无」—— 一眼看出哪天没人才是这张表的价值
                        <span className="text-slate-200 dark:text-slate-700">·</span>
                      ) : (
                        cell.map((s) => (
                          <div
                            key={s.id}
                            aria-label={`${d.name} ${hhmm(s.startsAt)} 班次`}
                            className={`mb-1 px-1.5 py-1 rounded text-xs ${
                              STATUS_STYLE[s.status] ?? 'bg-slate-100'
                            }`}
                          >
                            <div className="whitespace-nowrap">
                              {hhmm(s.startsAt)}–{hhmm(s.endsAt)}
                            </div>
                            <div className="flex items-center justify-center gap-1">
                              {/*
                                ★ 固定排班要标出来。不标的话它和单独排的那一天
                                  长得一模一样,而两者**改起来完全不同**:
                                  一个删掉只影响这一天,另一个要去停规则、
                                  影响以后每一个这个周几。
                              */}
                              {s.source === 'RULE' ? (
                                <span
                                  title="固定排班(每周)—— 在下面的「固定排班」里改"
                                  className="text-slate-500 dark:text-slate-400"
                                >
                                  固定
                                </span>
                              ) : (
                                <span>{s.statusLabel}</span>
                              )}
                              {/* 迟到几分钟直接标出来 —— 考勤要的是这个 */}
                              {s.lateMinutes > 0 && (
                                <span className="text-amber-700 dark:text-amber-400">
                                  迟 {s.lateMinutes}′
                                </span>
                              )}
                              {/*
                                ★ 固定排班这里**没有** ✕:它在库里还没有行,
                                  删不掉;而真正的操作是「停掉这条规则」,
                                  影响面大得多,要走下面那张清单。
                              */}
                              {canManage && s.source !== 'RULE' && s.status !== 'CHECKED_IN' && (
                                <button
                                  aria-label={`删除 ${d.name} ${hhmm(s.startsAt)} 的班次`}
                                  onClick={() => void remove(s)}
                                  className="text-slate-400"
                                >
                                  <InlineIcon icon={X} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active.length === 0 && <p className="mt-3 text-sm text-slate-500">还没有在职司机。</p>}

      {/*
        ★ 固定排班单独列一张表。
          周视图上只看得到「这一周」,而一条「每周六」的规则是长期的 ——
          光看周视图的人不知道这个班从哪来、到什么时候为止,
          更不知道去哪改。
      */}
      <section className="mt-6">
        <h2 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">
          固定排班
        </h2>
        <p className="mb-3 text-sm text-slate-500">
          「每周六」这种长期排班。不管哪个月,只要是这几个星期几就算排了班 ——
          上面的周视图里标着「固定」的格子就是它算出来的。
        </p>

        {rules.length === 0 ? (
          <p className="text-sm text-slate-400">还没有固定排班。用「+ 排班」里的「固定每周」建。</p>
        ) : (
          <div
            className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                       bg-white dark:bg-slate-800"
          >
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="p-3 text-left font-medium">司机</th>
                  <th className="p-3 text-left font-medium">星期</th>
                  <th className="p-3 text-left font-medium">时段</th>
                  <th className="p-3 text-left font-medium">生效</th>
                  {canManage && <th className="p-3" />}
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-700/50">
                    <td className="p-3">
                      <div className="text-slate-900 dark:text-slate-100">{r.driver.name}</div>
                      <div className="text-xs font-mono text-slate-400">{r.driver.driverNo}</div>
                    </td>
                    <td className="p-3 text-slate-700 dark:text-slate-300">
                      {r.weekdays.map((w) => `周${WEEKDAY[w]}`).join('、')}
                    </td>
                    <td className="p-3 text-slate-700 dark:text-slate-300">
                      {r.startTime}–{r.endTime}
                      {r.overnight && <span className="ml-1 text-xs text-amber-600">跨夜</span>}
                    </td>
                    <td className="p-3 text-slate-700 dark:text-slate-300">
                      {r.effectiveFrom} 起{/* ★ 「长期」要写出来 —— 空白会被当成「没填」 */}
                      {r.effectiveTo ? ` 至 ${r.effectiveTo}` : ' · 长期'}
                    </td>
                    {canManage && (
                      <td className="p-3 text-right">
                        <button
                          aria-label={`停掉 ${r.driver.name} 的固定排班`}
                          onClick={() => void removeRule(r)}
                          className="text-sm text-red-600 dark:text-red-400"
                        >
                          停掉
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {planning && (
        <PlanDialog
          drivers={active}
          defaultFrom={iso(days[0]!)}
          defaultTo={iso(days[6]!)}
          onClose={() => setPlanning(false)}
          /*
           * ★ 排完只刷新列表,**不关对话框**。
           *   关掉的话「建了几个、谁没排上」那一屏根本来不及看 ——
           *   而「谁没排上」正是排班时最需要知道的事。
           *   关闭由「完成」按钮负责。
           */
          onSaved={load}
        />
      )}
    </div>
  );
}

function PlanDialog({
  drivers,
  defaultFrom,
  defaultTo,
  onClose,
  onSaved,
}: {
  drivers: DriverRow[];
  defaultFrom: string;
  defaultTo: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  /**
   * 两种模式。
   *
   * ★ 为什么做成两种而不是一种:
   *
   *   - **固定每周**是一条长期规则(「每周六」),不管哪个月都算 ——
   *     它答的是「这个人平时哪几天上班」
   *   - **指定日期**是把某个日期范围展开成一条条具体班次 ——
   *     它答的是「这几天谁来补」
   *
   *   用一种模式硬撑另一种都难受:固定排班用「指定日期」表达就得每隔一段
   *   重新排一次(忘了就有人上不了线);临时加一天用规则表达又会永久生效。
   */
  const [mode, setMode] = useState<'RULE' | 'DATES'>('DATES');
  const [form, setForm] = useState({
    dateFrom: defaultFrom,
    dateTo: defaultTo,
    startTime: '09:00',
    endTime: '18:00',
  });
  /** 固定排班的生效区间。截止留空 = 长期 */
  const [effective, setEffective] = useState({ from: defaultFrom, to: '' });
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    created: number;
    skipped: Array<{ driverNo: string; date?: string; reason: string }>;
  } | null>(null);

  const overnight = form.endTime <= form.startTime;
  /*
   * ★ 固定排班**必须**选星期几,不能沿用「一个都不选表示每天」。
   *   固定排班的意思就是「固定在这几个周几」,空着是填漏了 ——
   *   当成每天处理的话,一次手滑把人排成全年无休,而界面上看起来只是没勾。
   */
  const needWeekdays = mode === 'RULE' && weekdays.length === 0;

  async function save() {
    if (busy || picked.length === 0 || needWeekdays) return;
    setBusy(true);
    setError(null);
    try {
      const body =
        mode === 'RULE'
          ? {
              driverIds: picked,
              weekdays,
              startTime: form.startTime,
              endTime: form.endTime,
              effectiveFrom: effective.from,
              // 空字符串要变成 null,不是 '' —— 接口那边校验的是日期格式
              effectiveTo: effective.to || null,
            }
          : { driverIds: picked, weekdays, ...form };
      setResult(
        await api<{
          created: number;
          skipped: Array<{ driverNo: string; date?: string; reason: string }>;
        }>(mode === 'RULE' ? '/shifts/rules' : '/shifts', { method: 'POST', body }),
      );
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '排班失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="排班"
        className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">排班</h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>

        {error && (
          <div
            className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        {result ? (
          <div className="space-y-2">
            <p className="text-slate-900 dark:text-slate-100">建了 {result.created} 个班次。</p>
            {/* ★ 跳过的要逐条说清楚 —— 只报「建了 12 个」的话,
                   谁没排上没人知道 */}
            {result.skipped.length > 0 && (
              <div
                className="rounded-lg border border-amber-200 dark:border-amber-900
                              bg-amber-50/60 dark:bg-amber-950/20 p-3 text-sm"
              >
                <div className="text-amber-800 dark:text-amber-300 mb-1">
                  {result.skipped.length} 个没建:
                </div>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-0.5">
                  {/* 固定排班那条路没有「哪一天」—— 它撞的是整条规则 */}
                  {result.skipped.slice(0, 10).map((s) => (
                    <li key={`${s.driverNo}-${s.date ?? 'rule'}`}>
                      {s.driverNo}
                      {s.date ? ` · ${s.date}` : ''} —— {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full h-10 rounded-lg bg-brand-600 text-white font-medium"
            >
              完成
            </button>
          </div>
        ) : (
          <>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  给谁排({picked.length})
                </span>
                <button
                  onClick={() =>
                    setPicked(picked.length === drivers.length ? [] : drivers.map((d) => d.id))
                  }
                  className="text-xs text-brand-600 dark:text-brand-500"
                >
                  {picked.length === drivers.length ? '全不选' : '全选'}
                </button>
              </div>
              <div
                className="max-h-40 overflow-y-auto rounded-lg border border-slate-200
                              dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700/50"
              >
                {drivers.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      aria-label={d.name}
                      checked={picked.includes(d.id)}
                      onChange={(e) =>
                        setPicked(
                          e.target.checked ? [...picked, d.id] : picked.filter((x) => x !== d.id),
                        )
                      }
                    />
                    <span className="text-slate-900 dark:text-slate-100">{d.name}</span>
                    <span className="text-xs text-slate-400 font-mono">{d.driverNo}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ★ 模式放在最上面:它决定了下面填什么,最后才切的话前面填的一半白填 */}
            <div
              role="tablist"
              aria-label="排班方式"
              className="flex gap-2 rounded-lg bg-slate-100 dark:bg-slate-900/60 p-1"
            >
              {(
                [
                  ['DATES', '指定日期', '临时加班、补一天、换班'],
                  ['RULE', '固定每周', '「每周六」,长期有效'],
                ] as const
              ).map(([key, label, hint]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={mode === key}
                  onClick={() => setMode(key)}
                  className={`flex-1 rounded-md px-3 py-2 text-left ${
                    mode === key
                      ? 'bg-white dark:bg-slate-700 shadow-sm'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}
                >
                  <span className="block text-sm text-slate-900 dark:text-slate-100">{label}</span>
                  <span className="block text-xs text-slate-400">{hint}</span>
                </button>
              ))}
            </div>

            {mode === 'DATES' ? (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">从</span>
                  <input
                    aria-label="开始日期"
                    type="date"
                    value={form.dateFrom}
                    onChange={(e) => setForm({ ...form, dateFrom: e.target.value })}
                    className={INPUT}
                  />
                </label>
                <label className="block">
                  <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">到</span>
                  <input
                    aria-label="结束日期"
                    type="date"
                    value={form.dateTo}
                    onChange={(e) => setForm({ ...form, dateTo: e.target.value })}
                    className={INPUT}
                  />
                </label>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                    从哪天起生效
                  </span>
                  <input
                    aria-label="生效日期"
                    type="date"
                    value={effective.from}
                    onChange={(e) => setEffective({ ...effective, from: e.target.value })}
                    className={INPUT}
                  />
                </label>
                <label className="block">
                  <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                    到哪天为止
                    <span className="text-slate-400"> —— 留空=长期</span>
                  </span>
                  <input
                    aria-label="截止日期"
                    type="date"
                    value={effective.to}
                    onChange={(e) => setEffective({ ...effective, to: e.target.value })}
                    className={INPUT}
                  />
                </label>
              </div>
            )}

            <div>
              <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                哪几天
                <span className="text-slate-400">
                  {mode === 'RULE' ? ' —— 固定排班必须选(至少一个)' : ' —— 一个都不选表示每天'}
                </span>
              </span>
              <div className="flex gap-1">
                {WEEKDAY.map((label, i) => (
                  <button
                    key={i}
                    aria-label={`周${label}`}
                    aria-pressed={weekdays.includes(i)}
                    onClick={() =>
                      setWeekdays(
                        weekdays.includes(i) ? weekdays.filter((x) => x !== i) : [...weekdays, i],
                      )
                    }
                    className={`flex-1 h-9 rounded-lg text-sm border ${
                      weekdays.includes(i)
                        ? 'border-brand-600 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                        : 'border-slate-300 dark:border-slate-600 text-slate-500'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">上班</span>
                <input
                  aria-label="上班时间"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  className={INPUT}
                />
              </label>
              <label className="block">
                <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">下班</span>
                <input
                  aria-label="下班时间"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  className={INPUT}
                />
              </label>
            </div>

            {/* ★ 跨夜班要说出来。不说的话,填 22:00–06:00 的人不确定
                   系统是当成 8 小时夜班,还是当成填错了 */}
            {overnight && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                下班时间早于上班时间 —— 按跨夜班处理,结束时间算第二天。
              </p>
            )}

            {needWeekdays && (
              <p className="text-xs text-red-600 dark:text-red-400">
                固定排班至少要选一个星期几 —— 不选的话这条规则一天都不会命中,
                而它在列表里看起来是排好了的。
              </p>
            )}

            {/*
              ★ 和上面那句同一个道理:填了时段却不起作用,是这一屏最容易
                让人误判的一处。放在按钮上方,填完正要点的时候看得到。
            */}
            <p className="text-xs text-amber-700 dark:text-amber-400">
              时段只是记录 —— 现在司机不打卡,生效的只有「这一天排了班」这件事。
            </p>
            <p className="text-xs text-slate-400">
              时间按门店所在时区算。
              {mode === 'RULE'
                ? '和他已有的固定排班时段重了会跳过,不会覆盖。'
                : '已经排过班(含固定排班)的那天会被跳过,不会覆盖。'}
            </p>

            <div className="flex gap-3 pt-1">
              <button
                onClick={onClose}
                className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                           text-slate-700 dark:text-slate-300"
              >
                取消
              </button>
              <button
                onClick={() => void save()}
                disabled={busy || picked.length === 0 || needWeekdays}
                className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
              >
                {busy
                  ? '排班中…'
                  : picked.length === 0
                    ? '先选司机'
                    : needWeekdays
                      ? '先选星期几'
                      : mode === 'RULE'
                        ? `给 ${picked.length} 人排固定班`
                        : `给 ${picked.length} 人排班`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const INPUT =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';
